import { type FormEvent, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Plus } from "lucide-react";
import { isBookCollectionKey } from "@/lib/knowledgeBaseRegistry";
import { suggestBookCollectionKey } from "@/lib/knowledgeBookRegistry";
import {
  collectionKeysForLibrary,
  humanizeLibraryId,
  isIntentionalLibrarySlug,
  normalizeLibraryId,
  resolveKnowledgeLibrary,
  suggestLibraryId,
} from "@/lib/knowledgeLibraryRegistry";
import { agentsHref } from "@/lib/atwAgentsHost";
import {
  bookPath,
  isKnowledgeLibraryId,
  libraryPath,
  OTHER_LIBRARY_ID,
  SUITE_LIBRARY_ID,
} from "@/lib/knowledgeModel";
import {
  KnowledgeChrome,
  KnowledgeError,
  KnowledgeNeedsBase,
  knowledgeCardGhostLinkClass,
  knowledgePrimaryLinkClass,
  knowledgeSecondaryLinkClass,
} from "@/pages/knowledge/KnowledgeChrome";
import { LibraryActionsMenu } from "@/pages/knowledge/LibraryActionsMenu";
import { useCustomBooks } from "@/pages/knowledge/useCustomBooks";
import { useCustomLibraries } from "@/pages/knowledge/useCustomLibraries";
import { useKnowledgeArticles } from "@/pages/knowledge/useKnowledgeArticles";
import { useKnowledgeBases } from "@/pages/knowledge/useKnowledgeBases";
import { useKnowledgeRouteBase } from "@/pages/knowledge/useKnowledgeRouteBase";

const fieldClass =
  "mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-white/20 dark:bg-zinc-900 dark:text-white";

export function LibraryPage() {
  const navigate = useNavigate();
  const { libraryId = "" } = useParams();
  const { books: customBooks, createBook, orgId, kbId } = useCustomBooks();
  const {
    createLibrary,
    updateLibrary,
    deleteLibrary,
    libraries: savedLibraries,
  } = useCustomLibraries();
  const { articles, loading, error } = useKnowledgeArticles();
  const { hasBases } = useKnowledgeBases();
  const baseId = useKnowledgeRouteBase();

  const [showBookForm, setShowBookForm] = useState(false);
  const [bookTitle, setBookTitle] = useState("");
  const [bookDescription, setBookDescription] = useState("");
  const [bookKeyDraft, setBookKeyDraft] = useState("");
  const [bookKeyTouched, setBookKeyTouched] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [promoteError, setPromoteError] = useState<string | null>(null);
  const [missingDescription, setMissingDescription] = useState("");
  const [showEditForm, setShowEditForm] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editError, setEditError] = useState<string | null>(null);
  const [editBusy, setEditBusy] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [menuError, setMenuError] = useState<string | null>(null);
  const [menuBusy, setMenuBusy] = useState(false);

  const counts = useMemo(() => {
    const map = new Map<string, number>();
    for (const article of articles) {
      for (const key of article.collectionKeys) {
        if (!isBookCollectionKey(key)) continue;
        map.set(key, (map.get(key) || 0) + 1);
      }
    }
    return map;
  }, [articles]);

  const bookKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const key of counts.keys()) keys.add(key);
    for (const book of customBooks) keys.add(book.collectionKey);
    return keys;
  }, [counts, customBooks]);

  const activeLibrary = useMemo(() => {
    if (!libraryId || libraryId === OTHER_LIBRARY_ID || libraryId === SUITE_LIBRARY_ID) {
      return null;
    }
    return resolveKnowledgeLibrary(orgId, libraryId, kbId, customBooks, bookKeys);
  }, [bookKeys, customBooks, kbId, libraryId, orgId, savedLibraries]);

  const customLibrary = activeLibrary;
  const isInferredLibrary = customLibrary?.source === "inferred";

  const customByKey = new Map(customBooks.map((book) => [book.collectionKey, book]));

  const isLegacyOther = libraryId === OTHER_LIBRARY_ID;
  const isKnownLegacy = isKnowledgeLibraryId(libraryId);

  const scopedKeys = useMemo(() => {
    if (isLegacyOther) return collectionKeysForLibrary(OTHER_LIBRARY_ID, bookKeys, customBooks);
    if (customLibrary) return collectionKeysForLibrary(customLibrary.id, bookKeys, customBooks);
    return [];
  }, [bookKeys, customBooks, customLibrary, isLegacyOther]);

  const title = isLegacyOther
    ? "Books library"
    : customLibrary?.title || (libraryId === SUITE_LIBRARY_ID ? "Library" : "Library");
  const description = isLegacyOther
    ? "Books not assigned to a named library, including keys from articles."
    : customLibrary?.description?.trim() ||
      (isInferredLibrary
        ? "Inferred from books and articles in this knowledge base. Save the library to pin a custom title and description."
        : customLibrary
          ? "Books assigned to this library. Add a book to group articles under a collection key."
          : "This library id is not used.");

  const rows = scopedKeys.map((key) => {
    const custom = customByKey.get(key);
    return {
      key,
      title: custom?.title || key,
      description:
        custom?.description?.trim() ||
        (custom
          ? "Book from New book. Membership is still a collection key on articles."
          : "Stored as a collection key on the article."),
      count: counts.get(key) || 0,
    };
  });

  function openBookForm() {
    setShowBookForm(true);
    setShowEditForm(false);
    setShowDeleteConfirm(false);
    setFormError(null);
    setBookTitle("");
    setBookDescription("");
    setBookKeyDraft("");
    setBookKeyTouched(false);
  }

  function openEditLibrary() {
    if (!customLibrary || customLibrary.source !== "registry") return;
    setShowBookForm(false);
    setShowDeleteConfirm(false);
    setShowEditForm(true);
    setEditTitle(customLibrary.title);
    setEditDescription(customLibrary.description || "");
    setEditError(null);
  }

  function openDeleteLibrary() {
    if (!customLibrary || customLibrary.source !== "registry") return;
    setShowBookForm(false);
    setShowEditForm(false);
    setShowDeleteConfirm(true);
    setDeleteError(null);
  }

  async function onSaveLibrary(event: FormEvent) {
    event.preventDefault();
    if (!customLibrary) return;
    setEditBusy(true);
    setEditError(null);
    const result = await updateLibrary(customLibrary.id, {
      title: editTitle,
      description: editDescription.trim() || undefined,
    });
    setEditBusy(false);
    if (!result.ok) {
      setEditError(result.error);
      return;
    }
    setShowEditForm(false);
  }

  async function onConfirmDeleteLibrary() {
    if (!customLibrary) return;
    setDeleteBusy(true);
    setDeleteError(null);
    const result = await deleteLibrary(customLibrary.id);
    setDeleteBusy(false);
    if (!result.ok) {
      setDeleteError(result.error);
      return;
    }
    void navigate(agentsHref(`/knowledge/bases/${encodeURIComponent(baseId || kbId)}/libraries`));
  }

  async function onDuplicateLibrary() {
    if (!customLibrary || customLibrary.source !== "registry") return;
    setMenuBusy(true);
    setMenuError(null);
    const copyTitle = `${customLibrary.title} copy`;
    const result = await createLibrary({
      title: copyTitle,
      description: customLibrary.description || undefined,
      id: suggestLibraryId(copyTitle),
    });
    setMenuBusy(false);
    if (!result.ok) {
      setMenuError(result.error);
      return;
    }
    void navigate(agentsHref(libraryPath(baseId || kbId, result.library.id)));
  }

  function onBookTitleChange(value: string) {
    setBookTitle(value);
    if (!bookKeyTouched) setBookKeyDraft(suggestBookCollectionKey(value));
  }

  async function onCreateBook(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    const result = await createBook({
      title: bookTitle,
      description: bookDescription.trim() || undefined,
      collectionKey: bookKeyDraft.trim() || undefined,
      libraryId: customLibrary?.id,
    });
    if (!result.ok) {
      setFormError(result.error);
      return;
    }
    setShowBookForm(false);
    void navigate(agentsHref(bookPath(baseId || kbId, result.book.collectionKey)));
  }

  async function onPromoteInferredLibrary() {
    if (!customLibrary) return;
    setPromoteError(null);
    const result = await createLibrary({
      title: customLibrary.title,
      id: customLibrary.id,
      description: customLibrary.description || undefined,
    });
    if (!result.ok) {
      setPromoteError(result.error);
    }
  }

  async function onCreateMissingLibrary() {
    const id = normalizeLibraryId(libraryId);
    setPromoteError(null);
    const result = await createLibrary({
      title: humanizeLibraryId(id),
      id,
      description: missingDescription.trim() || undefined,
    });
    if (!result.ok) {
      setPromoteError(result.error);
    }
  }

  if (!hasBases) {
    return (
      <KnowledgeChrome
        title={title}
        description="Libraries live inside a knowledge base. Create a knowledge base first."
        badge="Same articles"
        showBaseSwitcher={false}
      >
        <KnowledgeNeedsBase />
      </KnowledgeChrome>
    );
  }

  if (libraryId === SUITE_LIBRARY_ID || (!isLegacyOther && !customLibrary && isKnownLegacy)) {
    return (
      <KnowledgeChrome
        title="Library"
        description="This library id is not used."
        backHref="/knowledge/libraries"
        backLabel="Libraries"
      >
        <KnowledgeError message="Choose a library from the list or create one with New library." />
      </KnowledgeChrome>
    );
  }

  if (!isLegacyOther && !customLibrary) {
    if (isIntentionalLibrarySlug(libraryId)) {
      return (
        <KnowledgeChrome
          title={humanizeLibraryId(libraryId)}
          description="This library is not saved in the active knowledge base yet."
          badge={normalizeLibraryId(libraryId)}
          backHref="/knowledge/libraries"
          backLabel="Libraries"
        >
          {promoteError ? <KnowledgeError message={promoteError} /> : null}
          <div className="max-w-lg space-y-4 rounded-lg border border-dashed border-slate-300 bg-slate-50 dark:border-white/20 dark:bg-zinc-950/40 px-6 py-8">
            <p className="text-sm text-slate-600 dark:text-zinc-400">
              Create it here to add books under this id, or pick another library from the list.
            </p>
            <label className="block text-sm font-medium text-slate-700 dark:text-zinc-300">
              Description <span className="font-normal text-slate-500 dark:text-zinc-500">(optional)</span>
              <textarea
                value={missingDescription}
                onChange={(event) => setMissingDescription(event.target.value)}
                rows={2}
                className={fieldClass}
              />
            </label>
            <button
              type="button"
              onClick={onCreateMissingLibrary}
              className={knowledgePrimaryLinkClass}
            >
              Create this library
            </button>
          </div>
        </KnowledgeChrome>
      );
    }

    return (
      <KnowledgeChrome
        title="Library"
        description="This library was not found in the active knowledge base."
        backHref="/knowledge/libraries"
        backLabel="Libraries"
      >
        <KnowledgeError message="Library not found. It may belong to another knowledge base or was removed." />
      </KnowledgeChrome>
    );
  }

  return (
    <KnowledgeChrome
      title={title}
      description={description}
      badge={libraryId}
      backHref="/knowledge/libraries"
      backLabel="Libraries"
      actions={
        <>
          {customLibrary?.source === "registry" ? (
            <LibraryActionsMenu
              disabled={menuBusy}
              onLibraries={() => navigate(agentsHref("/knowledge/libraries"))}
              onDuplicate={() => void onDuplicateLibrary()}
              onEdit={openEditLibrary}
              onDelete={openDeleteLibrary}
            />
          ) : null}
          {isInferredLibrary ? (
            <button
              type="button"
              onClick={onPromoteInferredLibrary}
              className={knowledgeSecondaryLinkClass}
            >
              Save library
            </button>
          ) : null}
          {customLibrary ? (
            <button type="button" onClick={openBookForm} className={knowledgePrimaryLinkClass}>
              <Plus className="h-4 w-4" aria-hidden />
              New book
            </button>
          ) : null}
        </>
      }
    >
      {error ? <KnowledgeError message={error} /> : null}
      {promoteError ? <KnowledgeError message={promoteError} /> : null}
      {menuError ? <KnowledgeError message={menuError} /> : null}
      {formError && !showBookForm ? <KnowledgeError message={formError} /> : null}

      {showEditForm && customLibrary ? (
        <form
          onSubmit={onSaveLibrary}
          className="mb-6 max-w-lg space-y-4 rounded-lg border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-zinc-950/40"
        >
          <div>
            <h2 className="text-base font-semibold text-slate-900 dark:text-white">Edit library</h2>
            <p className="mt-1 font-mono text-xs text-slate-500 dark:text-zinc-500">{customLibrary.id}</p>
          </div>
          {editError ? <KnowledgeError message={editError} /> : null}
          <label className="block text-sm font-medium text-slate-700 dark:text-zinc-300">
            Title
            <input
              type="text"
              value={editTitle}
              onChange={(event) => setEditTitle(event.target.value)}
              required
              autoFocus
              className={fieldClass}
            />
          </label>
          <label className="block text-sm font-medium text-slate-700 dark:text-zinc-300">
            Description <span className="font-normal text-slate-500 dark:text-zinc-500">(optional)</span>
            <textarea
              value={editDescription}
              onChange={(event) => setEditDescription(event.target.value)}
              rows={2}
              className={fieldClass}
            />
          </label>
          <div className="flex flex-wrap gap-2">
            <button
              type="submit"
              disabled={editBusy || !editTitle.trim()}
              className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
            >
              {editBusy ? "Saving…" : "Save changes"}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowEditForm(false);
                setEditError(null);
              }}
              className={knowledgeSecondaryLinkClass}
            >
              Cancel
            </button>
          </div>
        </form>
      ) : null}

      {showDeleteConfirm && customLibrary ? (
        <div className="mb-6 max-w-lg space-y-4 rounded-lg border border-red-300 bg-red-50 p-5 shadow-sm dark:border-red-400/40 dark:bg-zinc-950/40">
          <div>
            <h2 className="text-base font-semibold text-slate-900 dark:text-white">Delete library</h2>
            <p className="mt-2 text-sm text-slate-600 dark:text-zinc-400">
              Delete <span className="font-medium text-slate-900 dark:text-white">{customLibrary.title}</span>?
              Books and articles stay in the store; only this library grouping is removed.
            </p>
          </div>
          {deleteError ? <KnowledgeError message={deleteError} /> : null}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={deleteBusy}
              onClick={() => void onConfirmDeleteLibrary()}
              className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-500 disabled:opacity-50"
            >
              {deleteBusy ? "Deleting…" : "Delete library"}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowDeleteConfirm(false);
                setDeleteError(null);
              }}
              className={knowledgeSecondaryLinkClass}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {showBookForm && customLibrary ? (
        <form
          onSubmit={onCreateBook}
          className="mb-6 max-w-lg space-y-4 rounded-lg border border-slate-200 bg-white dark:border-white/10 dark:bg-zinc-950/40 p-5"
        >
          <div>
            <h2 className="text-base font-semibold text-slate-900 dark:text-white">New book</h2>
            <p className="mt-1 text-sm text-slate-600 dark:text-zinc-400">
              Creates a book inside {customLibrary.title}. Articles you add use a collection key on
              the article row.
            </p>
          </div>
          {formError ? <KnowledgeError message={formError} /> : null}
          <label className="block text-sm font-medium text-slate-700 dark:text-zinc-300">
            Title
            <input
              type="text"
              value={bookTitle}
              onChange={(event) => onBookTitleChange(event.target.value)}
              required
              autoFocus
              className={fieldClass}
            />
          </label>
          <label className="block text-sm font-medium text-slate-700 dark:text-zinc-300">
            Description{" "}
            <span className="font-normal text-slate-500 dark:text-zinc-500">(optional)</span>
            <textarea
              value={bookDescription}
              onChange={(event) => setBookDescription(event.target.value)}
              rows={2}
              placeholder="Short purpose shown under the title"
              className={fieldClass}
            />
          </label>
          <label className="block text-sm font-medium text-slate-700 dark:text-zinc-300">
            Key <span className="font-normal text-slate-500 dark:text-zinc-500">(optional slug)</span>
            <input
              type="text"
              value={bookKeyDraft}
              onChange={(event) => {
                setBookKeyTouched(true);
                setBookKeyDraft(event.target.value);
              }}
              className={`${fieldClass} font-mono text-xs`}
            />
          </label>
          <div className="flex flex-wrap gap-2">
            <button
              type="submit"
              disabled={!bookTitle.trim()}
              className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
            >
              Create book
            </button>
            <button
              type="button"
              onClick={() => {
                setShowBookForm(false);
                setFormError(null);
              }}
              className={knowledgeSecondaryLinkClass}
            >
              Cancel
            </button>
          </div>
        </form>
      ) : null}

      {loading ? <p className="text-sm text-slate-500 dark:text-zinc-500">Loading books…</p> : null}
      {!loading && !showBookForm && rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 dark:border-white/15 dark:bg-zinc-950/40 px-6 py-10 text-center">
          <p className="text-sm text-slate-600 dark:text-zinc-400">No books in this library yet.</p>
          {customLibrary ? (
            <button
              type="button"
              onClick={openBookForm}
              className={`mt-4 inline-flex ${knowledgePrimaryLinkClass}`}
            >
              <Plus className="h-4 w-4" aria-hidden />
              New book
            </button>
          ) : (
            <Link to={agentsHref("/knowledge/books")} className={`mt-4 inline-flex ${knowledgePrimaryLinkClass}`}>
              New book
            </Link>
          )}
        </div>
      ) : null}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {rows.map((row) => (
          <article
            key={row.key}
            className="flex flex-col rounded-lg border border-dashed border-slate-300 bg-slate-50 dark:border-white/15 dark:bg-zinc-950/40 p-5"
          >
            <div className="flex items-start justify-between gap-2">
              <h2 className="text-base font-semibold text-slate-900 dark:text-white">{row.title}</h2>
              <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600 dark:bg-white/10 dark:text-zinc-300">
                {row.count.toLocaleString()} {row.count === 1 ? "article" : "articles"}
              </span>
            </div>
            <p className="mt-1 font-mono text-[11px] text-slate-500 dark:text-zinc-500">{row.key}</p>
            <p className="mt-2 flex-1 text-sm text-slate-600 dark:text-zinc-400">{row.description}</p>
            <Link to={agentsHref(bookPath(baseId || kbId, row.key))} className={knowledgeCardGhostLinkClass}>
              Manage articles
            </Link>
          </article>
        ))}
      </div>
    </KnowledgeChrome>
  );
}
